import React, { useEffect, useMemo, useState } from "react";
import { Users, Plus, Shield, Trash2, Edit3, RefreshCw, Building2, CheckCircle2, XCircle, KeyRound, Filter, Mail, MailCheck, MailWarning } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABEL = {
  admin: "Administrator",
  staff: "ISOQuest Management",
  consultant: "Consultant",
  client_admin: "Client Administrator",
  client_employee: "Client Employee",
};
const ROLE_TONE = {
  admin: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  staff: "bg-indigo-100 text-indigo-700 border-indigo-200",
  consultant: "bg-blue-100 text-blue-700 border-blue-200",
  client_admin: "bg-emerald-100 text-emerald-700 border-emerald-200",
  client_employee: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], role_defaults: {}, roles: [] });
  const [clients, setClients] = useState([]);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("internal");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [accs, cat, cl, perm] = await Promise.all([
        endpoints.listAccounts(),
        endpoints.modulesCatalog(),
        endpoints.listClients().catch(() => []),
        endpoints.myPermissions(),
      ]);
      setAccounts(accs);
      setCatalog(cat);
      setClients(cl);
      setMe(perm);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load accounts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const canManage = (target) => {
    const r = me?.role;
    if (r === "admin") return true;
    if (r === "staff") return ["staff", "consultant", "client_admin", "client_employee"].includes(target.role);
    if (r === "consultant") return ["client_admin", "client_employee"].includes(target.role);
    return false;
  };

  const internal = accounts.filter(a => ["admin", "staff", "consultant"].includes(a.role));
  const clientAccs = accounts.filter(a => ["client_admin", "client_employee"].includes(a.role));

  const remove = async (a) => {
    if (!window.confirm(`Delete account ${a.email}?`)) return;
    try {
      await endpoints.deleteAccount(a.id);
      toast.success("Account deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const resendInvite = async (a) => {
    try {
      const updated = await endpoints.resendAccountInvite(a.id);
      setAccounts((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
      toast.success(`Invite email sent to ${a.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invite — please try again shortly");
    }
  };

  const list = tab === "internal" ? internal : clientAccs;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 text-white flex items-center justify-center shadow-md">
            <Shield className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Account management</h1>
            <p className="text-xs text-slate-500">Invite team members, set roles and control what every user can see and do.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="accounts-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          {me && (me.role === "admin" || me.role === "staff" || me.role === "consultant") && me.can_invite_users && (
            <Button className="brand-gradient text-white" onClick={() => setOpenNew(true)} data-testid="accounts-new">
              <Plus className="h-4 w-4 mr-1" /> Invite user
            </Button>
          )}
        </div>
      </div>

      {me && (
        <div className="mb-4 flex flex-wrap gap-2 items-center text-xs text-slate-500">
          <Badge className="rounded-full">You: {me.full_name || me.email || me.role}</Badge>
          <Badge variant="outline" className={`rounded-full ${ROLE_TONE[me.role] || ""}`}>{ROLE_LABEL[me.role] || me.role}</Badge>
          <Badge variant="outline" className="rounded-full">Modules: {me.allowed_modules?.length ?? 0}</Badge>
          {!me.can_invite_users && <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200">Cannot invite users</Badge>}
        </div>
      )}

      <div className="flex gap-1 border-b mb-4">
        <button onClick={() => setTab("internal")} data-testid="accounts-tab-internal"
          className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "internal" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          ISOQuest team ({internal.length})
        </button>
        <button onClick={() => setTab("clients")} data-testid="accounts-tab-clients"
          className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "clients" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Client users ({clientAccs.length})
        </button>
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {!loading && list.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              No accounts yet. {me?.can_invite_users && <span>Click <b>Invite user</b> to add one.</span>}
            </div>
          )}
          {!loading && list.length > 0 && (
            <div className="divide-y">
              {list.map(a => (
                <div key={a.id} data-testid={`acc-row-${a.id}`} className="p-4 flex flex-wrap items-center gap-3">
                  <div className="h-10 w-10 rounded-xl brand-gradient text-white flex items-center justify-center font-semibold shrink-0">
                    {(a.full_name || a.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold text-sm">{a.full_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{a.email}</span>
                      {a.client_slug && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {a.client_slug}</span>}
                      {a.assigned_client_slugs?.length > 0 && <span>· {a.assigned_client_slugs.length} client{a.assigned_client_slugs.length > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className={`rounded-full ${ROLE_TONE[a.role] || ""}`}>{ROLE_LABEL[a.role] || a.role}</Badge>
                  <Badge variant="outline" className="rounded-full">
                    {a.effective_permissions?.allowed_modules?.length ?? 0} modules
                  </Badge>
                  {a.active ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 rounded-full"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-500"><XCircle className="h-3 w-3 mr-1" /> Disabled</Badge>
                  )}
                  {a.email_sent ? (
                    <Badge className="bg-sky-100 text-sky-700 border-sky-200 rounded-full" title={a.invite_sent_at ? `Invite sent ${a.invite_sent_at.slice(0,10)}` : "Invite sent"}>
                      <MailCheck className="h-3 w-3 mr-1" /> Invited
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200" title="Invite email has not been delivered">
                      <MailWarning className="h-3 w-3 mr-1" /> Not sent
                    </Badge>
                  )}
                  {canManage(a) && (
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => resendInvite(a)}
                        data-testid={`acc-resend-${a.id}`}
                        title={a.email_sent ? "Resend invite email" : "Send invite email"}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`acc-edit-${a.id}`}><Edit3 className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(a)} data-testid={`acc-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AccountModal
        open={openNew || !!editing}
        editing={editing}
        onOpenChange={(o) => { if (!o) { setOpenNew(false); setEditing(null); } }}
        catalog={catalog}
        clients={clients}
        me={me}
        defaultScope={tab === "clients" ? "client" : "internal"}
        onSaved={() => { setOpenNew(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function AccountModal({ open, editing, onOpenChange, catalog, clients, me, defaultScope, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const availableTargetRoles = useMemo(() => {
    const r = me?.role;
    if (r === "admin") return ["admin", "staff", "consultant", "client_admin", "client_employee"];
    if (r === "staff") return ["staff", "consultant", "client_admin", "client_employee"];
    if (r === "consultant") return ["client_admin", "client_employee"];
    return [];
  }, [me]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        email: editing.email,
        full_name: editing.full_name,
        role: editing.role,
        client_slug: editing.client_slug || "",
        assigned_client_slugs: editing.assigned_client_slugs || [],
        active: editing.active,
        permissions: {
          allowed_modules: editing.permissions?.allowed_modules?.length
            ? editing.permissions.allowed_modules
            : editing.effective_permissions?.allowed_modules || [],
          can_write: editing.effective_permissions?.can_write ?? true,
          can_delete: editing.effective_permissions?.can_delete ?? true,
          can_export: editing.effective_permissions?.can_export ?? true,
          can_invite_users: editing.effective_permissions?.can_invite_users ?? false,
        },
      });
    } else {
      const defRole = defaultScope === "client" ? "client_admin" : (availableTargetRoles[0] || "consultant");
      const base = catalog.role_defaults?.[defRole] || {};
      setForm({
        email: "", full_name: "", role: defRole, client_slug: "", assigned_client_slugs: [], active: true,
        permissions: {
          allowed_modules: base.allowed_modules || [],
          can_write: base.can_write ?? true,
          can_delete: base.can_delete ?? true,
          can_export: base.can_export ?? true,
          can_invite_users: base.can_invite_users ?? false,
        },
      });
    }
  }, [open, editing, defaultScope, catalog, availableTargetRoles]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPerm = (k, v) => setForm(f => ({ ...f, permissions: { ...f.permissions, [k]: v } }));
  const toggleModule = (key) => setForm(f => {
    const s = new Set(f.permissions.allowed_modules);
    if (s.has(key)) s.delete(key); else s.add(key);
    return { ...f, permissions: { ...f.permissions, allowed_modules: Array.from(s) } };
  });
  const setRoleWithDefaults = (role) => {
    const base = catalog.role_defaults?.[role] || {};
    setForm(f => ({
      ...f, role,
      permissions: {
        allowed_modules: base.allowed_modules || [],
        can_write: base.can_write ?? true,
        can_delete: base.can_delete ?? true,
        can_export: base.can_export ?? true,
        can_invite_users: base.can_invite_users ?? false,
      },
    }));
  };

  if (!form) return null;
  const isClientRole = form.role === "client_admin" || form.role === "client_employee";
  const canHaveAssignedClients = ["admin", "staff", "consultant"].includes(form.role);
  const roleDefaults = catalog.role_defaults?.[form.role] || { allowed_modules: [] };
  const grouped = (catalog.modules || []).reduce((acc, m) => {
    // Consultants can't grant modules they themselves don't have.
    if (me?.role === "consultant" && !me.allowed_modules.includes(m.key)) return acc;
    if (!roleDefaults.allowed_modules.includes(m.key)) return acc;
    (acc[m.group] = acc[m.group] || []).push(m);
    return acc;
  }, {});

  const submit = async () => {
    if (!form.email.trim() || !form.full_name.trim()) return toast.error("Name and email required");
    if (isClientRole && !form.client_slug) return toast.error("Pick a client tenant");
    setSaving(true);
    try {
      if (isEdit) {
        await endpoints.updateAccount(editing.id, {
          full_name: form.full_name,
          role: form.role,
          client_slug: isClientRole ? form.client_slug : null,
          assigned_client_slugs: canHaveAssignedClients ? form.assigned_client_slugs : [],
          active: form.active,
          permissions: form.permissions,
        });
        toast.success("Account updated");
      } else {
        await endpoints.createAccount({
          email: form.email,
          full_name: form.full_name,
          role: form.role,
          client_slug: isClientRole ? form.client_slug : null,
          assigned_client_slugs: canHaveAssignedClients ? form.assigned_client_slugs : [],
          active: form.active,
          permissions: form.permissions,
        });
        toast.success("Account created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="acc-modal" className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit account" : "Invite user"}</DialogTitle>
          <DialogDescription>{isEdit ? "Update role, tenant and permissions." : "Create an account, assign a role and fine-tune what they can see and do."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={e => set("email", e.target.value)} disabled={isEdit} data-testid="acc-email" placeholder="name@example.com" />
            </div>
            <div>
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={e => set("full_name", e.target.value)} data-testid="acc-name" placeholder="Jane Doe" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={setRoleWithDefaults} disabled={isEdit && me?.role === "consultant"}>
                <SelectTrigger data-testid="acc-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableTargetRoles.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.active} onCheckedChange={v => set("active", v)} data-testid="acc-active" />
              <Label className="text-sm">Active</Label>
            </div>
          </div>

          {isClientRole && (
            <div>
              <Label>Client tenant</Label>
              <Select value={form.client_slug} onValueChange={v => set("client_slug", v)}>
                <SelectTrigger data-testid="acc-client-slug"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">This user can only access data for the selected client.</p>
            </div>
          )}

          {canHaveAssignedClients && (
            <div>
              <Label>Allocated clients {form.role !== "consultant" && <span className="text-[10px] text-slate-400 font-normal">· admin & staff already see all; this is for workload tracking</span>}</Label>
              <div className="grid grid-cols-2 gap-1.5 rounded-lg border p-2 max-h-40 overflow-y-auto">
                {clients.map(c => {
                  const on = (form.assigned_client_slugs || []).includes(c.slug);
                  return (
                    <label key={c.slug} className="flex items-center gap-2 text-xs rounded px-2 py-1 hover:bg-slate-50 cursor-pointer">
                      <Checkbox checked={on} onCheckedChange={() => set("assigned_client_slugs",
                        on ? form.assigned_client_slugs.filter(s => s !== c.slug) : [...form.assigned_client_slugs, c.slug])} />
                      <span className="truncate">{c.company_name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                {form.role === "consultant"
                  ? "Consultant can only see and manage these client tenants."
                  : "Shown on each client's hub. Admin/staff keep full access regardless."}
              </p>
            </div>
          )}

          <div>
            <Label>Action permissions</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {[
                { k: "can_write",        label: "Write / edit" },
                { k: "can_delete",       label: "Delete" },
                { k: "can_export",       label: "Export" },
                { k: "can_invite_users", label: "Invite users" },
              ].map(({ k, label }) => (
                <label key={k} className="flex items-center gap-2 rounded-lg border p-2 text-xs">
                  <Switch checked={!!form.permissions[k]} onCheckedChange={v => setPerm(k, v)} data-testid={`acc-perm-${k}`} />
                  {label}
                </label>
              ))}
            </div>
            {form.role === "client_admin" && form.permissions.can_invite_users && (
              <p className="text-[11px] text-amber-600 mt-1">Note: your requirement is that only consultants can add client accounts. This toggle is stored but the backend still restricts invites of client roles to consultant/staff/admin.</p>
            )}
          </div>

          <div>
            <Label>Module access</Label>
            <p className="text-[11px] text-slate-500 mb-2">Uncheck the modules this user should not see. {me?.role === "consultant" && "You can only grant modules you have."}</p>
            <div className="space-y-3">
              {Object.entries(grouped).map(([group, mods]) => (
                <div key={group} className="rounded-xl border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{group}</div>
                    <div className="flex gap-1">
                      <button type="button" className="text-[10px] text-indigo-600 hover:underline"
                        onClick={() => setForm(f => ({ ...f, permissions: { ...f.permissions, allowed_modules: Array.from(new Set([...f.permissions.allowed_modules, ...mods.map(m => m.key)])) } }))}>Select all</button>
                      <button type="button" className="text-[10px] text-slate-500 hover:underline"
                        onClick={() => setForm(f => ({ ...f, permissions: { ...f.permissions, allowed_modules: f.permissions.allowed_modules.filter(k => !mods.some(m => m.key === k)) } }))}>Clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {mods.map(m => {
                      const on = form.permissions.allowed_modules.includes(m.key);
                      return (
                        <label key={m.key} className="flex items-center gap-2 text-xs rounded px-2 py-1 hover:bg-slate-50 cursor-pointer">
                          <Checkbox checked={on} onCheckedChange={() => toggleModule(m.key)} data-testid={`acc-mod-${m.key}`} />
                          <span>{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="brand-gradient text-white" onClick={submit} disabled={saving} data-testid="acc-save">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
